import { Component, ElementRef, Injector, ViewChild } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Observable, from } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ComponentBase } from "../../../../../core/components/abstractions/component-base";
import { NamedBlob } from "../../../../../core/models/files/named-blob";
import { PdfViewerButtonOptions } from "../../../../../core/models/dialogs/pdf-viewer-button-options";

@Component({
  selector: "xml-viewer-dialog",
  templateUrl: "./xml-viewer-dialog.component.html",
  styleUrls: ["./xml-viewer-dialog.component.scss"]
})
export class XmlViewerDialogComponent extends ComponentBase {
  public onClose: Function = null;
  public fileName: string;
  public xmlContent: string;
  public buttonOptions: PdfViewerButtonOptions;

  @ViewChild("downloadLink", { static: false })
  private downloadLink: ElementRef;

  private fileSource: Observable<NamedBlob>;
  private file: Blob;

  constructor(private injector: Injector) {
    super();
  }

  public tryLoadXml(source: Observable<NamedBlob>, buttonOptions: PdfViewerButtonOptions, onComplete?: Function) {
    this.fileSource = source;
    this.buttonOptions = buttonOptions;

    if (!source) {
      this.closeDialog();
      return;
    }

    this.fileSource
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: res => {
          this.loadXml(res, buttonOptions, onComplete);
        },
        error: () => this.closeDialog()
      });
  }

  public loadXml(source: NamedBlob, buttonOptions: PdfViewerButtonOptions, onComplete?: Function) {
    if (!source) {
      this.closeDialog();
      return;
    }

    this.file = source.blob;
    this.fileName = source.fileName;
    this.buttonOptions = buttonOptions;

    from(this.file.text())
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: text => {
          this.xmlContent = this.formatXml(text);
          if (onComplete) {
            onComplete();
          }
        },
        error: () => this.closeDialog()
      });
  }

  public cancel(): void {
    this.closeDialog();
  }

  public closeDialog(): void {
    if (this.onClose) {
      this.onClose();
    }
    else {
      const dialogRef = this.injector.get(NgbActiveModal);
      dialogRef.close();
    }
  }

  public download(): void {
    this.removeHover("download-btn");

    if (!this.file) {
      return;
    }

    const actualFileName: string = this.fileName ? this.fileName : `preview-${new Date().getTime()}.xml`;
    const link = this.downloadLink.nativeElement;
    link.href = window.URL.createObjectURL(this.file);
    link.download = actualFileName;
    link.click();
    window.URL.revokeObjectURL(link.href);
  }

  public print(): void {
    this.removeHover("print-btn");

    if (!this.xmlContent) {
      return;
    }

    const printWindow = window.open("", "", "width=1400, height=900");
    if (!printWindow) {
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`<html><head><title>${this.fileName || "XML"}</title></head><body><pre></pre></body></html>`);
    printWindow.document.close();

    const preElement = printWindow.document.querySelector("pre");
    if (preElement) {
      preElement.textContent = this.xmlContent;
    }

    printWindow.focus();
    printWindow.print();
  }

  private formatXml(xml: string): string {
    if (!xml) {
      return "";
    }

    const formatted = xml
      .replace(/\r\n/g, "\n")
      .replace(/>\s*</g, ">\n<");
    let pad = 0;

    return this.normalizeXmlLines(formatted)
      .map(line => {
        let indent = "";
        const isClosingTag = /^<\//.test(line);
        const isOpeningTag = /^<[^!?][^>]*[^\/]>$/.test(line);

        if (isClosingTag) {
          pad = Math.max(pad - 1, 0);
        }

        indent = "  ".repeat(pad);

        if (isOpeningTag && !line.includes("</")) {
          pad += 1;
        }

        return `${indent}${line}`;
      })
      .join("\n");
  }

  private normalizeXmlLines(xml: string): string[] {
    const lines: string[] = [];
    let pendingTag: string = null;

    xml.split("\n").forEach(rawLine => {
      let line = rawLine.trim();
      if (!line) {
        return;
      }

      if (pendingTag) {
        pendingTag = `${pendingTag} ${line}`;
        if (!line.includes(">")) {
          return;
        }

        line = pendingTag;
        pendingTag = null;
      }
      else if (line.startsWith("<") && !line.includes(">")) {
        pendingTag = line;
        return;
      }

      lines.push(line);
    });

    return lines;
  }

  private removeHover(elementId: string) {
    const element = document.getElementById(elementId);
    if (element) {
      element.blur();
    }
  }
}
